import { useState, useMemo, useCallback, useEffect, useRef } from "react";

import type { FlowSection, FlowScreen, FlowAnswers } from "@/shared/constants/flowTypes";
import { trackEvent, EventType } from "@/shared/utils/core";

interface UseFlowSectionProps {
  section: FlowSection;
  initialStep?: number;
  initialAnswers?: FlowAnswers;
}

interface ValidationErrors {
  [fieldId: string]: string;
}

interface UseFlowSectionReturn {
  currentStep: number;
  totalSteps: number;
  progress: number;
  currentScreen: FlowScreen;
  answers: FlowAnswers;
  errors: ValidationErrors;
  setAnswer: (questionId: string, value: string | string[]) => void;
  goNext: (options?: { skipValidation?: boolean }) => void;
  goBack: () => void;
  goToScreen: (screenId: string, updatedAnswers?: FlowAnswers) => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

export function useFlowSection({
  section,
  initialStep = 0,
  initialAnswers = {},
}: UseFlowSectionProps): UseFlowSectionReturn {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [answers, setAnswers] = useState<FlowAnswers>(initialAnswers);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const answersRef = useRef<FlowAnswers>(initialAnswers);
  const completedInSessionRef = useRef(new Set<string>());
  const intendedScreenIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    answersRef.current = answers;
  });

  const screenEnteredAtRef = useRef<number>(Date.now());
  const peakProgressRef = useRef(0);
  const analyticsFlowRef = useRef(section.analyticsFlow);
  analyticsFlowRef.current = section.analyticsFlow;
  const resolveAnalyticsFlow = useCallback(
    () => analyticsFlowRef.current?.(answersRef.current),
    [],
  );

  const prevInitialAnswersRef = useRef<FlowAnswers>({});
  const prevSectionIdRef = useRef(section.id);
  useEffect(() => {
    if (prevSectionIdRef.current !== section.id) {
      setCurrentStep(initialStep);
      setAnswers(initialAnswers);
      setErrors({});
      peakProgressRef.current = 0;
      completedInSessionRef.current = new Set();
      prevInitialAnswersRef.current = {};
      prevSectionIdRef.current = section.id;
    }
  }, [section.id, initialStep, initialAnswers]);
  useEffect(() => {
    const prev = prevInitialAnswersRef.current;
    prevInitialAnswersRef.current = initialAnswers;
    const changed: FlowAnswers = {};
    for (const [key, value] of Object.entries(initialAnswers)) {
      if (prev[key] !== value) {
        changed[key] = value as string;
      }
    }
    if (Object.keys(changed).length > 0) {
      setAnswers((a) => ({ ...a, ...changed }));
    }
  }, [initialAnswers]);

  const visibleScreens = useMemo(() => {
    return section.screens.filter((screen) => {
      if (!screen.shouldDisplay) return true;
      return screen.shouldDisplay({ answers, currentStep });
    });
  }, [section.screens, answers, currentStep]);

  useEffect(() => {
    const targetId = intendedScreenIdRef.current;
    if (!targetId) return;
    const idx = visibleScreens.findIndex((s) => s.id === targetId);
    if (idx >= 0 && idx !== currentStepRef.current) {
      setCurrentStep(idx);
    }
  }, [visibleScreens]);

  const totalSteps = visibleScreens.length;
  const currentScreen = visibleScreens[currentStep] ?? visibleScreens[0];

  const prevScreenIdRef = useRef<string | undefined>(undefined);
  const isReturningRef = useRef(Object.keys(initialAnswers).length > 0);
  const isResumeRef = useRef(initialStep > 0);
  const currentStepRef = useRef(currentStep);
  currentStepRef.current = currentStep;
  useEffect(() => {
    if (currentScreen?.id && currentScreen.id !== prevScreenIdRef.current) {
      screenEnteredAtRef.current = Date.now();
      prevScreenIdRef.current = currentScreen.id;
      const flow = resolveAnalyticsFlow();
      if (flow) {
        trackEvent(EventType.ONBOARDING_STEP_VIEWED, {
          flow,
          step_id: currentScreen.id,
          step_number: currentStepRef.current + 1,
          is_resume: isResumeRef.current,
        });
      } else {
        trackEvent(EventType.ONBOARDING_SCREEN_VIEWED, {
          screen_id: currentScreen.id,
          section_id: section.id,
          step_number: currentStepRef.current,
          is_returning: isReturningRef.current,
        });
      }
      isReturningRef.current = false;
      isResumeRef.current = false;
    }
  }, [currentScreen?.id, section.id, resolveAnalyticsFlow]);
  const progressTotal = section.expectedTotalSteps ?? totalSteps;
  if (peakProgressRef.current === 0 && currentStep > 0 && progressTotal > 0) {
    peakProgressRef.current = (currentStep / progressTotal) * 100;
  }
  const progress = Math.min(peakProgressRef.current, 100);
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  const validateCurrentScreen = useCallback((): ValidationErrors => {
    const newErrors: ValidationErrors = {};

    if (currentScreen?.question?.required) {
      const question = currentScreen.question;

      if (question.type === "multiple_choice") {
        const answer = answers[question.id];
        if (!answer || (typeof answer === "string" && answer.trim() === "")) {
          newErrors[question.id] = "Please select an option";
        }
      }

      if (question.type === "multi_select") {
        const answer = answers[question.id];
        if (!answer || !Array.isArray(answer) || answer.length === 0) {
          newErrors[question.id] = "Please select at least one option";
        }
      }

      if (question.type === "input_field" && question.fields) {
        question.fields.forEach((field) => {
          const fieldValue = answers[field.id];
          const hasValue = typeof fieldValue === "string" && fieldValue.trim() !== "";

          if (field.required && !hasValue) {
            newErrors[field.id] = field.requiredMessage
              ? field.requiredMessage({})
              : field.label
                ? `${field.label} is required`
                : "This field is required";
            return;
          }

          if (hasValue && field.validate && !field.validate(fieldValue as string)) {
            newErrors[field.id] = field.errorMessage ?? "Please enter a valid value";
          }
        });
      }

      if (question.type === "text_field") {
        const answer = answers[question.id];
        if (!answer || (typeof answer === "string" && answer.trim() === "")) {
          newErrors[question.id] = "This field is required";
        }
      }
    }

    if (currentScreen?.validate) {
      const customErrors = currentScreen.validate(answers);
      Object.assign(newErrors, customErrors);
    }

    return newErrors;
  }, [currentScreen, answers]);

  const setAnswer = useCallback((questionId: string, value: string | string[]) => {
    const next = { ...answersRef.current, [questionId]: value };
    answersRef.current = next;
    setAnswers(next);
    setErrors((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      if (prev[questionId]) {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      }
      if (currentScreen?.validate) {
        const revalidated = currentScreen.validate(next);
        return Object.fromEntries(
          Object.entries(prev).filter(([key]) => revalidated[key]),
        );
      }
      return prev;
    });
  }, [currentScreen]);

  const goNext = useCallback(
    (options?: { skipValidation?: boolean }) => {
      if (!options?.skipValidation) {
        const validationErrors = validateCurrentScreen();

        if (Object.keys(validationErrors).length > 0) {
          setErrors(validationErrors);
          const flow = resolveAnalyticsFlow();
          if (flow) {
            trackEvent(EventType.ONBOARDING_VALIDATION_FAILED, {
              flow,
              step_id: currentScreen.id,
              field_ids: Object.keys(validationErrors).join(","),
            });
          } else {
            trackEvent(EventType.FORM_VALIDATION_FAILED, {
              screen_id: currentScreen.id,
              section_id: section.id,
              field_ids: Object.keys(validationErrors).join(", "),
            });
          }
          return;
        }
      }

      setErrors({});

      const screen = currentScreen;
      const questionId = screen?.question?.id;

      if (screen.customComponent && !questionId && !answersRef.current[screen.id]) {
        const next = { ...answersRef.current, [screen.id]: "completed" };
        answersRef.current = next;
        setAnswers(next);
        completedInSessionRef.current.add(screen.id);
      }

      const currentAnswers = answersRef.current;
      const answer = questionId ? currentAnswers[questionId] : undefined;

      const freshScreens = section.screens.filter((s) => {
        if (!s.shouldDisplay) return true;
        return s.shouldDisplay({ answers: currentAnswers, currentStep });
      });
      const freshTotal = freshScreens.length;
      const hasNextStep = currentStep < freshTotal - 1;
      let nextStep = currentStep + 1;
      while (
        hasNextStep &&
        nextStep < freshTotal - 1 &&
        freshScreens[nextStep]?.skipOnBack &&
        completedInSessionRef.current.has(freshScreens[nextStep].id) &&
        currentAnswers[freshScreens[nextStep].id] === "completed"
      ) {
        nextStep++;
      }

      const timeOnScreen = Math.round((Date.now() - screenEnteredAtRef.current) / 1000);
      const flow = resolveAnalyticsFlow();
      if (flow) {
        trackEvent(EventType.ONBOARDING_STEP_COMPLETED, {
          flow,
          step_id: screen.id,
          step_number: currentStep + 1,
          time_on_step_seconds: timeOnScreen,
          next_step_id: hasNextStep ? freshScreens[nextStep]?.id : undefined,
        });
      } else {
        trackEvent(EventType.ONBOARDING_SCREEN_COMPLETED, {
          screen_id: screen.id,
          section_id: section.id,
          step_number: currentStep,
          time_on_screen_seconds: timeOnScreen,
        });

        if (questionId && answer !== undefined) {
          trackEvent(EventType.ONBOARDING_ANSWER_SUBMITTED, {
            screen_id: screen.id,
            question_id: questionId,
            question_type: screen.question?.type,
            answer_value: Array.isArray(answer) ? answer.join(", ") : answer,
          });
        }
      }

      if (section.onStepComplete) {
        section.onStepComplete(screen.id, questionId, answer, currentAnswers);
      }

      if (hasNextStep) {
        const nextProgress = progressTotal > 0 ? (nextStep / progressTotal) * 100 : 0;
        if (nextProgress > peakProgressRef.current) {
          peakProgressRef.current = nextProgress;
        }
        intendedScreenIdRef.current = freshScreens[nextStep]?.id;
        setCurrentStep(nextStep);
      } else if (section.onComplete) {
        section.onComplete(currentAnswers);
      }
    },
    [
      currentStep,
      progressTotal,
      section,
      currentScreen,
      validateCurrentScreen,
      resolveAnalyticsFlow,
    ],
  );

  const goBack = useCallback(() => {
    if (currentStep > 0) {
      const timeOnScreen = Math.round((Date.now() - screenEnteredAtRef.current) / 1000);
      const flow = resolveAnalyticsFlow();
      if (flow) {
        trackEvent(EventType.ONBOARDING_STEP_BACK_CLICKED, {
          flow,
          step_id: currentScreen.id,
          time_on_step_seconds: timeOnScreen,
        });
      } else {
        trackEvent(EventType.ONBOARDING_SCREEN_COMPLETED, {
          screen_id: currentScreen.id,
          section_id: section.id,
          step_number: currentStep,
          time_on_screen_seconds: timeOnScreen,
          navigation_direction: "back",
        });
      }
      setErrors({});

      let targetStep = currentStep - 1;
      while (targetStep >= 0 && visibleScreens[targetStep]?.skipOnBack) {
        targetStep--;
      }
      if (targetStep < 0) return;
      intendedScreenIdRef.current = visibleScreens[targetStep]?.id;
      setCurrentStep(targetStep);
      peakProgressRef.current = progressTotal > 0 ? (targetStep / progressTotal) * 100 : 0;
    }
  }, [currentStep, currentScreen, section.id, visibleScreens, progressTotal, resolveAnalyticsFlow]);

  const goToScreen = useCallback(
    (screenId: string, updatedAnswers?: FlowAnswers) => {
      const answersToUse = updatedAnswers ?? answers;

      const timeOnScreen = Math.round((Date.now() - screenEnteredAtRef.current) / 1000);
      const flow = resolveAnalyticsFlow();
      if (flow) {
        trackEvent(EventType.ONBOARDING_STEP_COMPLETED, {
          flow,
          step_id: currentScreen.id,
          step_number: currentStep + 1,
          time_on_step_seconds: timeOnScreen,
          next_step_id: screenId,
        });
      } else {
        trackEvent(EventType.ONBOARDING_SCREEN_COMPLETED, {
          screen_id: currentScreen.id,
          section_id: section.id,
          step_number: currentStep,
          time_on_screen_seconds: timeOnScreen,
          jumped_to: screenId,
        });
      }

      if (section.onStepComplete) {
        const screen = currentScreen;
        const questionId = screen?.question?.id;
        const answer = questionId ? answersToUse[questionId] : undefined;
        section.onStepComplete(screen.id, questionId, answer, answersToUse);
      }

      const newVisibleScreens = section.screens.filter((screen) => {
        if (!screen.shouldDisplay) return true;
        return screen.shouldDisplay({ answers: answersToUse, currentStep: 0 });
      });

      const targetIndex = newVisibleScreens.findIndex((s) => s.id === screenId);
      if (targetIndex >= 0) {
        if (updatedAnswers) {
          answersRef.current = updatedAnswers;
          setAnswers(updatedAnswers);
        }
        const newTotal = section.expectedTotalSteps ?? newVisibleScreens.length;
        const targetProgress = newTotal > 0 ? (targetIndex / newTotal) * 100 : 0;
        if (targetIndex > currentStep) {
          if (targetProgress > peakProgressRef.current) {
            peakProgressRef.current = targetProgress;
          }
        } else {
          peakProgressRef.current = targetProgress;
        }
        intendedScreenIdRef.current = screenId;
        setCurrentStep(targetIndex);
        setErrors({});
      }
    },
    [answers, section, currentScreen, currentStep, resolveAnalyticsFlow],
  );

  return {
    currentStep,
    totalSteps,
    progress,
    currentScreen,
    answers,
    errors,
    setAnswer,
    goNext,
    goBack,
    goToScreen,
    isFirstStep,
    isLastStep,
  };
}
